import { Injectable , inject} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AppConfigService } from 'app/core/config/app-config.service';
import { FeatureSettingsService } from '@app/services/feature-settings.service';
import { AuthRepository } from '@app/core/auth/auth.repository';
import { BehaviorSubject, Observable, Subject, of, firstValueFrom } from 'rxjs';
import { StepAudioCaptureService } from './step-audio-capture.service';
import { DictionaryService } from '@app/core/studema/dictionary-lookup/dictionary.service';
import { CompletionProof, matchesCompletion, hasCurrentPass } from './completion-proof';
import { catchError, map, tap } from 'rxjs/operators';
import { setCourseAiMode, setCourseStepPosition, setStepAiDebug, CourseAiMode, urlStepId } from './ai-step-chat-config';

/** The widget saying it has finished the line the host paused for. */
const QUIZ_PRAISE_DONE_EVENT = 'ai-chat-quiz-praise-done';

/** Longest the course waits on the AI before advancing anyway. */
const QUIZ_PRAISE_MAX_WAIT_MS = 8000;
export interface CourseDictionaryEntry {
  word: string;
  definitions: string[];
}

export interface GlobalAiChatConfig {
  environment: string;
  agentId: string;
  apiBaseUrl: string;  // Required - no fallback
  content?: string;
  lang: string;  // Always "en"
  contentLang: string;  // Always "en"
  role: 'child' | 'executive';  // Default from this.appConfig.get('ai_chat_role')
  autoStart: boolean;  // Always true for sidebar (connect once)
  muteAi: boolean;  // Mute when in listener mode or switching
  debug: boolean;
  fullMode: boolean;
  showWidget: boolean;
  /** Monotonic visit id — every step open (incl. back) bumps this so the widget always applies config. */
  flowSeq?: number;
  /** Alias for hosts/logs that prefer stepVisitId naming. */
  stepVisitId?: number;
  completionAttemptId?: string;
}

/**
 * Where the student is inside a multi-item step. Sent whenever the position
 * changes so the AI coaches the example actually on screen, instead of
 * treating the whole deck as one thing to read.
 */
export interface StepProgress {
  /** Zero-based position of the visible item. */
  slideIndex: number;
  totalSlides: number;
  /** Plain text of the visible item, HTML already stripped. */
  slideText: string;
  isLastSlide: boolean;
  /** Presentation galleries wait behind a Start button before slide 1. */
  presentationState?: 'start' | 'slides' | 'done';
  /** A drill or final exam has been graded; the AI congratulates a clean run. */
  quizDone?: boolean;
  correctPercentage?: number;
  wrongCount?: number;
}

/** The AI asking the host to turn the page for the student. */
export interface SlideRequest {
  /** Absolute slide to show. */
  index?: number;
  direction?: 'next' | 'prev';
}

/**
 * How long a mandatory gate waits for an AI verdict before unlocking Done
 * anyway. Matches the course-entry welcome timeout in this service: long
 * enough for a slow step, short enough that a dead session is not a wall.
 */
const GATE_FAIL_OPEN_MS = 90_000;

/**
 * How long a step change waits for the next step's own config before parking
 * on the listener. Covers a normal step API load, so moving between two AI
 * steps goes straight from one agent to the next.
 */
const STEP_CHANGE_PARK_MS = 2500;

@Injectable({
  providedIn: 'root'
})
export class GlobalAiChatService {
  readonly appConfig = inject(AppConfigService);
  private readonly featureSettings = inject(FeatureSettingsService);
  private readonly http = inject(HttpClient);
  private readonly audioCapture = inject(StepAudioCaptureService);
  /** The course's WordPress dictionary stores, for widget word lookups. */
  private readonly dictionary = inject(DictionaryService);
  readonly verificationUnavailable$ = new BehaviorSubject<string | null>(null);
  readonly memorySync$ = new BehaviorSubject<{ state: string; studentId?: string; studentCourseId?: string }>({ state: 'saved' });
  private lastVerification: { id: string; outcome: string } | null = null;
  private readonly authRepository = inject(AuthRepository);
  // Get apiBaseUrl (return empty string if not configured – AI chat will be disabled)
  private getApiBaseUrl(): string {
    return this.appConfig.get('ai_api_baseurl_chat') || '';
  }

  private defaultConfig: GlobalAiChatConfig = {
    environment: "webcomponent", // so we no that the data comes from a webcomponent in react ai-chat
    agentId: 'agent.listener',  // ← Start with listener agent
    apiBaseUrl: this.getApiBaseUrl(),
    lang: 'en',  // ← Always "en"
    contentLang: 'en',  // ← Always "en"
    role: (this.appConfig.get('ai_chat_role') as 'child' | 'executive') || 'executive',  // ← From environment, default 'executive'
    autoStart: true,  // ← Always true - connect once at startup
    muteAi: true,  // ← Mute in listener mode (silent agent)
    debug: false,
    fullMode: true,
    showWidget: true,
    flowSeq: 0,
    stepVisitId: 0,
  };

  // Current widget configuration (observable)
  private configSubject = new BehaviorSubject<GlobalAiChatConfig>(this.defaultConfig);
  public config$: Observable<GlobalAiChatConfig> = this.configSubject.asObservable();

  /** Monotonic counter for every config push (step open / back / next). */
  private flowSeqCounter = 0;

  /**
   * Debounce reset→listener so step ngOnDestroy does not briefly hop to
   * listener before the next step's updateConfig arrives.
   */
  private resetToListenerTimer: ReturnType<typeof setTimeout> | null = null;

  /** Timestamp of last updateConfig — used to ignore stale destroy resets. */
  private lastUpdateConfigAt = 0;

  /** Step whose config the widget currently holds (null for listener parks). */
  private configStepId: string | null = null;

  /** Last position reported inside the open step, replayed after its cutover. */
  private lastStepProgress: { stepId: string | null; progress: StepProgress } | null = null;

  /** Step-load timing: when the student opened a step, and when each visit was pushed. */
  private stepNavigationAt = 0;
  private configPushedAt = new Map<number, number>();

  /** Lesson worker agent is active — Done waits for ai-chat-flow-terminal. */
  private aiFlowActive = false;

  /**
   * First AI worker step after entering academy uses welcome-back (resume);
   * later steps in the same visit use enter (no re-welcome).
   */
  private academyWelcomeUsed = false;

  /** Course title for resume welcome-back lines. */
  private academyCourseTitle = '';
  private academyCourseId = '';

  /**
   * True while the course-entry “Welcome back…” line is the active flow.
   * Blocks resetToListener / defers later step configs until it finishes.
   */
  private courseEntryWelcomeActive = false;

  /** student_course_step_id the course-entry welcome was spoken for. */
  private courseEntryWelcomeStepId: string | null = null;

  /** Latest step_id from the academy URL (compact layout). */
  private activeStepId: string | null = null;

  /** Step config that arrived while course-entry welcome was still speaking. */
  private pendingConfigAfterWelcome: Partial<GlobalAiChatConfig> | null = null;

  /** Step id that owns pendingConfigAfterWelcome (if known). */
  private pendingConfigStepId: string | null = null;

  /** Safety: if welcome never terminals (session stuck), unblock deferred steps. */
  private courseEntryWelcomeTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Visit-scoped course lexicon accumulated from step payloads (word +
   * definition). Injected into every AI content JSON so the widget may only
   * use course definitions — never an external dictionary.
   */
  private courseDictionary: CourseDictionaryEntry[] = [];

  private readyForDoneSubject = new BehaviorSubject<boolean>(true);
  public readyForDone$: Observable<boolean> = this.readyForDoneSubject.asObservable();

  /**
   * Per-course override for mandatory AI validation, resolved server-side
   * onto the enrollment. null = no course opinion, inherit company → system.
   */
  private courseAiStepGate: boolean | null = null;

  /** Course opt-in for keeping the student's microphone audio on AI steps. */
  private courseAiRecordAudio = false;

  /** Course's LLM turn analysis: off, shadow (store only) or live. */
  private courseAiSemanticTurns: 'off' | 'shadow' | 'live' = 'off';

  /**
   * How much AI the current course runs, resolved server-side onto the
   * enrollment. null outside /academy/*, where no course has an opinion and
   * the company flag alone decides.
   */
  private courseAiMode: CourseAiMode | null = null;

  /** The enrollment has delivered its AI settings for this academy visit. */
  private courseAiSettingsLoaded = false;
  private courseAiSettingsSubject = new Subject<void>();
  public courseAiSettings$: Observable<void> = this.courseAiSettingsSubject.asObservable();

  /**
   * AI may be used right now, as a stream.
   *
   * The course mode arrives with the enrollment, a moment after the layout has
   * already built itself, so the shell cannot read this once in a constructor
   * and be right — it has to be told when the answer changes.
   */
  private aiActiveSubject = new BehaviorSubject<boolean>(false);
  public aiActive$: Observable<boolean> = this.aiActiveSubject.asObservable();

  /**
   * Done is blocked right now. Emitted rather than polled so the button can
   * actually look disabled instead of silently refusing the click.
   */
  private doneBlockedSubject = new BehaviorSubject<boolean>(false);
  public doneBlocked$: Observable<boolean> = this.doneBlockedSubject.asObservable();

  /**
   * A mandatory gate that depends on a live realtime session can strand a
   * student behind a broken microphone or a dropped websocket. This timer
   * unlocks Done when no verdict arrives in time.
   */
  private gateFailOpenTimer: ReturnType<typeof setTimeout> | null = null;

  /** Set when the gate opened itself rather than the AI opening it. */
  private gateFailedOpen = false;

  /**
   * Where the student is *inside* a step: which gallery slide is showing.
   * Kept off `config$` on purpose — a config push restarts the AI flow, and
   * turning a page is not a new step. The layout forwards these to the
   * widget as a lightweight event instead.
   */
  private stepProgressSubject = new Subject<StepProgress>();
  public stepProgress$: Observable<StepProgress> =
    this.stepProgressSubject.asObservable();

  /** The AI asking the host to move the carousel for the student. */
  private slideRequestSubject = new Subject<SlideRequest>();
  public slideRequest$: Observable<SlideRequest> =
    this.slideRequestSubject.asObservable();

  /**
   * AI may be used right now. Every AI behaviour hangs off this: widget
   * config, Done gate, and AI-session audio capture. The answer microphone
   * (app-voice-input) is deliberately not routed through here — a student
   * without an AI tutor still records and saves spoken answers.
   */
  private get isEnabled(): boolean {
    return (
      this.featureSettings.isFeatureEnabled('feature_ai') &&
      !this.isOptedOut() &&
      this.courseAiMode !== 'off'
    );
  }

  /**
   * Public read of the same answer, for the shell and the step types.
   *
   * Everything that used to ask AppConfig for `feature_ai` asks this instead:
   * AppConfig only sees the static licence file, so it could not see a company
   * override, a student opt-out, or a course switched to no AI.
   */
  isAiActive(): boolean {
    return this.isEnabled;
  }

  /** Simple mode speaks one authored sentence per step and gates nothing. */
  isSimpleAiMode(): boolean {
    return this.isEnabled && this.courseAiMode === 'simple';
  }

  /**
   * This course runs no AI at all.
   *
   * Distinct from the student having opted out: there is nothing here to opt
   * back into, so the sidebar offers no AI switch rather than a dead one.
   */
  isCourseAiOff(): boolean {
    return this.courseAiMode === 'off';
  }

  /**
   * This course is known to run AI (simple or extended).
   *
   * False until the enrollment has arrived: before that the mode is unknown,
   * and `isEnabled` counts unknown as "not off".
   */
  isCourseAiOn(): boolean {
    return this.isEnabled && this.courseAiSettingsLoaded;
  }

  private publishAiActive(): void {
    const active = this.isEnabled;
    if (this.aiActiveSubject.value !== active) {
      this.aiActiveSubject.next(active);
    }
    // The capture service cannot ask this one — it is the dependency, not the
    // dependant — so the answer is pushed to it on every change.
    this.audioCapture.setRecordingEnabled(this.isAudioRecordingEnabled());
  }

  /**
   * Re-read the flags and emit.
   *
   * This service can be constructed before sign-in, when the company settings
   * are not loaded yet and the first published answer is therefore "no AI".
   * A subscriber that comes up after login calls this so it starts from the
   * truth rather than that stale first emission.
   */
  refreshAiActive(): void {
    this.publishAiActive();
  }

  /**
   * The student switched the AI tutor off for themselves. Read from the auth
   * store rather than cached, so a service instantiated before sign-in can
   * never serve a stale answer.
   */
  isOptedOut(): boolean {
    return Number(this.authRepository.getUser()?.ai_opted_out || 0) === 1;
  }

  constructor() {
    setStepAiDebug(!!this.appConfig.get('ai_chat_debug'));
    window.addEventListener('ai-chat-memory-sync', ((event: CustomEvent) => {
      if (String(event.detail?.studentId) !== String(this.authRepository.getUser()?.person_id)) return;
      this.memorySync$.next(event.detail);
      this.publishDoneBlocked();
    }) as EventListener);
    window.addEventListener('ai-chat-verification', ((event: CustomEvent) => {
      if (!this.matchesCurrentCompletion(event.detail)) return;
      if (typeof event.detail.verificationAttemptId === 'string') {
        this.lastVerification = { id: event.detail.verificationAttemptId, outcome: event.detail.outcome };
        this.readyForDoneSubject.next(false);
        this.publishDoneBlocked();
      }
      if (event.detail?.unavailable) this.reportAiSessionUnavailable('The verification service is unavailable; your answer has not been marked wrong.');
    }) as EventListener);
    // The widget cannot reach WordPress: the JWT, the wpUrl and the dev proxy
    // all live here. It asks, this answers, and the word joins the visit
    // lexicon so the rest of the course step already has it.
    window.addEventListener('ai-chat-define-word', ((event: CustomEvent) => {
      // preventDefault is the handshake: it tells the widget the request has
      // been taken, so a page without this bridge fails fast instead of leaving
      // the student waiting out the widget's timeout.
      event.preventDefault();
      void this.answerWordLookup(event.detail);
    }) as EventListener);
    this.publishAiActive();
    if (this.isEnabled) {
      console.log('🎯 GlobalAiChatService initialized with listener agent');
      console.log('📍 API Base URL:', this.defaultConfig.apiBaseUrl);
      console.log('🔇 Listener mode: Muted (silent agent)');
    }
  }

  /**
   * Persist the student's own AI on/off choice.
   *
   * The API notifies the supervisor on opt-out, so this must not be called
   * optimistically: local state only flips once the server confirms.
   */
  setAiOptedOut(optedOut: boolean, studentCourseId?: number | null): Observable<boolean> {
    const user = this.authRepository.getUser();
    const studentId = Number(user?.person_id || 0);
    if (studentId <= 0) {
      console.warn('[StepAI] cannot save AI opt-out — no student id');
      return of(false);
    }

    const url = `${this.appConfig.get('serverUrl')}/v1/students/${studentId}/ai-opt-out`;
    const body: Record<string, unknown> = { opted_out: optedOut };
    if (studentCourseId) {
      body['student_course_id'] = studentCourseId;
    }

    return this.http.put<any>(url, body).pipe(
      map((res) => res?.status === 'OK'),
      tap((ok) => {
        if (!ok) return;
        this.authRepository.setUser({ ...user, ai_opted_out: optedOut ? 1 : 0 });
        this.applyOptedOut(optedOut);
        console.log('[StepAI] ai opt-out saved', { optedOut });
      }),
      catchError((err) => {
        console.error('[StepAI] ai opt-out save failed', err);
        return of(false);
      })
    );
  }

  /**
   * Local switch for the AI side of the app.
   *
   * Opting out mid-step must also release a mandatory Done gate: the AI that
   * was going to open it is gone, and the supervisor reviews the step instead.
   */
  private applyOptedOut(optedOut: boolean): void {
    if (optedOut) {
      this.aiFlowActive = false;
      this.clearGateFailOpen();
      this.gateFailedOpen = false;
      this.readyForDoneSubject.next(true);
      this.cancelPendingResetToListener();
      this.courseEntryWelcomeActive = false;
      this.courseEntryWelcomeStepId = null;
      this.pendingConfigAfterWelcome = null;
      this.pendingConfigStepId = null;
      if (this.courseEntryWelcomeTimer != null) {
        clearTimeout(this.courseEntryWelcomeTimer);
        this.courseEntryWelcomeTimer = null;
      }
    }
    this.publishDoneBlocked();
    this.publishAiActive();
  }

  private upsertCourseDictionary(word: string, definitions: string[]): void {
    const w = String(word || '').trim();
    const defs = (definitions || [])
      .map((d) => String(d || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim())
      .filter(Boolean);
    if (!w || !defs.length) return;
    const key = w.toLowerCase();
    const existing = this.courseDictionary.find((e) => e.word.toLowerCase() === key);
    if (!existing) {
      this.courseDictionary.push({ word: w, definitions: [...defs] });
      return;
    }
    for (const d of defs) {
      if (!existing.definitions.some((x) => x.toLowerCase() === d.toLowerCase())) {
        existing.definitions.push(d);
      }
    }
  }

  /**
   * Look a word up in the course's WordPress stores for the widget.
   *
   * No `sources` are named on purpose: `store-config.php` decides the order, so
   * this course's own definitions file is consulted before anything else. The
   * answer is upserted into the visit lexicon, which means it also rides along
   * in the next content push and never has to be looked up twice.
   *
   * Every failure is answered too — the widget is waiting on a reply and falls
   * back on its own when it hears nothing.
   */
  private async answerWordLookup(detail: unknown): Promise<void> {
    const request = (detail || {}) as { requestId?: string; word?: string; language?: string };
    const requestId = String(request.requestId || '');
    const word = String(request.word || '').trim();
    if (!requestId || !word) return;

    const reply = (payload: Record<string, unknown>): void => {
      window.dispatchEvent(
        new CustomEvent('ai-chat-word-definition', { detail: { requestId, word, ...payload } })
      );
    };

    try {
      const res: any = await firstValueFrom(
        this.dictionary.lookup(word, String(request.language || 'en') || 'en')
      );
      const list: any[] = Array.isArray(res?.definitions)
        ? res.definitions
        : Array.isArray(res)
          ? res
          : [];
      const hit = list.find((d) => String(d?.definition || d?.html || '').trim());
      const definition = String(hit?.definition || hit?.html || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      if (!definition) {
        console.log('[StepAI] word lookup found nothing', { word });
        reply({ error: 'not_found' });
        return;
      }
      this.upsertCourseDictionary(word, [definition]);
      console.log('[StepAI] word lookup answered', { word, source: hit?.source });
      reply({ definition, source: String(hit?.source || 'wordDictionary') });
    } catch (err) {
      console.warn('[StepAI] word lookup failed', { word, err });
      reply({ error: 'lookup_failed' });
    }
  }

  /**
   * Ingest word/definition from step content and attach the visit lexicon.
   */
  private ingestAndAttachCourseDictionary(content?: string): string | undefined {
    if (!content || !String(content).trim()) return content;
    try {
      const parsed = JSON.parse(content);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return content;
      }

      if (Array.isArray(parsed.courseDictionary)) {
        for (const entry of parsed.courseDictionary) {
          if (entry?.word && Array.isArray(entry.definitions)) {
            this.upsertCourseDictionary(String(entry.word), entry.definitions.map(String));
          }
        }
      }

      const word = typeof parsed.word === 'string' ? parsed.word.trim() : '';
      const defs: string[] = [];
      if (typeof parsed.definition === 'string' && parsed.definition.trim()) {
        defs.push(parsed.definition);
      }
      if (typeof parsed.definitionHtml === 'string' && parsed.definitionHtml.trim()) {
        defs.push(parsed.definitionHtml);
      }
      if (Array.isArray(parsed.definitions)) {
        for (const d of parsed.definitions) {
          if (typeof d === 'string' && d.trim()) defs.push(d);
        }
      }
      // Infer word from common command phrasing when only a definition is present.
      let resolvedWord = word;
      if (!resolvedWord && typeof parsed.command === 'string') {
        const m = parsed.command.match(/\bword\s+["']?([A-Za-z-]+)["']?/i);
        if (m) resolvedWord = m[1];
      }
      if (resolvedWord && defs.length) {
        this.upsertCourseDictionary(resolvedWord, defs);
      }

      if (this.courseDictionary.length) {
        parsed.courseDictionary = this.courseDictionary.map((e) => ({
          word: e.word,
          definitions: [...e.definitions],
        }));
        return JSON.stringify(parsed);
      }
      return content;
    } catch {
      return content;
    }
  }

  getCourseDictionary(): CourseDictionaryEntry[] {
    return this.courseDictionary.map((e) => ({
      word: e.word,
      definitions: [...e.definitions],
    }));
  }

  /**
   * Mute / showWidget-only updates must not bump flowSeq — that re-accepts
   * the previous step's agent+command and re-speaks it after navigation.
   */
  private isCosmeticConfigUpdate(config: Partial<GlobalAiChatConfig>): boolean {
    const keys = Object.keys(config).filter(
      (k) => (config as Record<string, unknown>)[k] !== undefined
    );
    if (keys.length === 0) return true;
    const cosmetic = new Set([
      'muteAi',
      'showWidget',
      'debug',
      'fullMode',
      'environment',
      'lang',
      'contentLang',
      'role',
      'autoStart',
      'apiBaseUrl',
    ]);
    return keys.every((k) => cosmetic.has(k));
  }

  /** Track the step currently shown in the academy secondary outlet. */
  setActiveStepId(stepId: string | null | undefined): void {
    if (!this.isEnabled) return;
    const next = stepId != null && String(stepId).trim() !== '' ? String(stepId) : null;
    // While course-entry welcome owns a step, ignore stale URL step ids from
    // the previous session until the router catches up to the open step.
    if (
      next &&
      this.courseEntryWelcomeActive &&
      this.courseEntryWelcomeStepId &&
      next !== this.courseEntryWelcomeStepId
    ) {
      console.log('[StepAI] ignore activeStepId — course entry welcome owns step', {
        ignored: next,
        welcomeStepId: this.courseEntryWelcomeStepId,
      });
      return;
    }
    this.activeStepId = next;
  }

  /**
   * Step navigation: drop deferred Gallery/etc configs so the previous
   * command cannot speak again, and schedule (not force) a listener park.
   */
  notifyStepIdChanged(
    previousStepId: string | null,
    newStepId: string
  ): void {
    if (!this.isEnabled) return;
    this.activeStepId = newStepId;

    // Stale deferred configs from a previous outlet must never survive navigation.
    if (
      this.pendingConfigAfterWelcome &&
      (!this.pendingConfigStepId || this.pendingConfigStepId !== newStepId)
    ) {
      console.log('[StepAI] drop pendingConfigAfterWelcome — step changed', {
        previousStepId,
        newStepId,
        pendingStepId: this.pendingConfigStepId,
      });
      this.pendingConfigAfterWelcome = null;
      this.pendingConfigStepId = null;
    }

    // Landing on the step we just welcomed for (URL catch-up after checksheet
    // navigate) must not cut the welcome mid-line.
    if (
      this.courseEntryWelcomeActive &&
      this.courseEntryWelcomeStepId &&
      this.courseEntryWelcomeStepId === newStepId
    ) {
      console.log('[StepAI] keep course entry welcome — navigated onto welcomed step', {
        previousStepId,
        newStepId,
      });
      return;
    }

    if (this.courseEntryWelcomeActive) {
      this.courseEntryWelcomeActive = false;
      this.courseEntryWelcomeStepId = null;
      if (this.courseEntryWelcomeTimer != null) {
        clearTimeout(this.courseEntryWelcomeTimer);
        this.courseEntryWelcomeTimer = null;
      }
      console.log('[StepAI] release course entry welcome — step changed', {
        previousStepId,
        newStepId,
      });
    }
    // Deliberately debounced rather than an immediate listener push. Every
    // navigation used to hop through the silent listener first, which rebuilds
    // the whole agent set, takes a speakLock and waits out two idle settles
    // before the step's real agent gets a word in. The next step's own config
    // cancels this timer, so the hop only happens when nothing else claims
    // the widget (a step with no AI, or a slow load). A step that turns out to
    // have no AI parks sooner by calling resetToListener itself.
    this.lastStepProgress = null;
    this.resetToListener(STEP_CHANGE_PARK_MS);
  }

  /**
   * Update widget configuration (called by step components)
   * Simplified: Directly applies config without listener transfer
   * Disconnect/reconnect is handled by layout component on step changes
   */
  updateConfig(
    config: Partial<GlobalAiChatConfig> & { sourceStepId?: string | number | null }
  ): void {
    if (!this.isEnabled) return;

    const sourceStepId =
      config.sourceStepId != null && String(config.sourceStepId).trim() !== ''
        ? String(config.sourceStepId)
        : null;
    // Strip host-only field — never push it into widget config.
    const { sourceStepId: _sourceStepId, ...configRest } = config;
    config = configRest;

    const cosmeticOnly = this.isCosmeticConfigUpdate(config);

    // Ignore zombie step components that finish loading after navigation.
    // The router updates `activeStepId` after the step component may already
    // have pushed (course switch), so the URL is the tie-breaker.
    if (
      !cosmeticOnly &&
      sourceStepId &&
      this.activeStepId &&
      sourceStepId !== this.activeStepId &&
      urlStepId() === sourceStepId
    ) {
      console.log('[StepAI] adopt config step — activeStepId was stale', {
        sourceStepId,
        staleActiveStepId: this.activeStepId,
      });
      this.activeStepId = sourceStepId;
    }
    if (
      !cosmeticOnly &&
      sourceStepId &&
      this.activeStepId &&
      sourceStepId !== this.activeStepId
    ) {
      console.log('[StepAI] ignore config — not current step', {
        sourceStepId,
        activeStepId: this.activeStepId,
        agentId: config.agentId,
      });
      return;
    }
    // On a step, a new visit must say which step it is for; otherwise a
    // component that has not resolved its step yet can start a flow for none.
    if (!cosmeticOnly && !sourceStepId && this.activeStepId) {
      console.log('[StepAI] ignore config — missing sourceStepId', {
        activeStepId: this.activeStepId,
        agentId: config.agentId,
      });
      return;
    }

    // Do not cut the course-entry welcome mid-line. Queue the next step
    // config and apply it when resume_v1 terminals.
    // Cosmetic mute/showWidget updates may apply immediately (no re-speak).
    if (
      !cosmeticOnly &&
      this.courseEntryWelcomeActive &&
      !this.contentHasCourseEntry(config.content)
    ) {
      // Require a step id during welcome — otherwise a zombie outlet can
      // poison pending and speak the previous session's agent after resume.
      if (!sourceStepId) {
        console.log('[StepAI] ignore deferred config — missing sourceStepId', {
          agentId: config.agentId,
          welcomeStepId: this.courseEntryWelcomeStepId,
        });
        return;
      }
      if (
        this.courseEntryWelcomeStepId &&
        sourceStepId !== this.courseEntryWelcomeStepId
      ) {
        console.log('[StepAI] ignore deferred config — wrong step for welcome', {
          sourceStepId,
          welcomeStepId: this.courseEntryWelcomeStepId,
          agentId: config.agentId,
        });
        return;
      }
      this.pendingConfigAfterWelcome = {
        ...(this.pendingConfigAfterWelcome || {}),
        ...config,
      };
      this.pendingConfigStepId = sourceStepId;
      console.log('[StepAI] defer config until course entry welcome finishes', {
        agentId: config.agentId,
        muteAi: config.muteAi,
        sourceStepId: this.pendingConfigStepId,
      });
      return;
    }

    this.cancelPendingResetToListener();
    this.lastUpdateConfigAt = Date.now();

    const currentConfig = this.configSubject.value;
    
    // Determine if we should mute based on agent
    const shouldMute = this.shouldMuteForAgent(config.agentId || currentConfig.agentId);

    const visitId = cosmeticOnly
      ? currentConfig.flowSeq ?? this.flowSeqCounter
      : (++this.flowSeqCounter);

    const contentWithDict =
      config.content !== undefined
        ? this.ingestAndAttachCourseDictionary(config.content)
        : currentConfig.content;
    
    // Ensure lang and contentLang are always "en"
    const newConfig: GlobalAiChatConfig = {
      ...currentConfig,
      ...config,
      content: contentWithDict,
      lang: 'en',  // ← Always "en"
      contentLang: 'en',  // ← Always "en"
      role: config.role || (this.appConfig.get('ai_chat_role') as 'child' | 'executive') || 'executive',  // ← From environment, default 'executive'
      autoStart: true,  // ← Always true (will reconnect)
      muteAi: config.muteAi !== undefined ? config.muteAi : shouldMute,
      apiBaseUrl: config.apiBaseUrl || currentConfig.apiBaseUrl,
      flowSeq: visitId,
      stepVisitId: visitId,
      completionAttemptId: cosmeticOnly ? currentConfig.completionAttemptId : crypto.randomUUID(),
    };
    if (!cosmeticOnly) {
      this.lastVerification = null;
      this.configStepId = sourceStepId ?? this.activeStepId;
      this.configPushedAt.set(visitId, Date.now());
      if (this.configPushedAt.size > 20) {
        this.configPushedAt.delete(this.configPushedAt.keys().next().value as number);
      }
    }
    
    // Warn if apiBaseUrl is not set (AI chat will not function)
    if (!newConfig.apiBaseUrl) {
      console.warn('⚠️ ai_api_baseurl_chat is not configured – AI chat disabled');
    }

    const agentId = newConfig.agentId || 'agent.listener';
    this.aiFlowActive = agentId !== 'agent.listener';
    // New AI step: Done gated until flow terminal. Listener / non-AI: allow Done.
    this.readyForDoneSubject.next(!this.aiFlowActive);
    this.armGateFailOpen();
    this.publishDoneBlocked();
    
    console.log('[StepAI] config.push', {
      agentId: newConfig.agentId,
      flowSeq: visitId,
      cosmeticOnly,
      muteAi: newConfig.muteAi,
      hasContent: !!newConfig.content,
      contentLength: newConfig.content?.length || 0,
      aiFlowActive: this.aiFlowActive,
      readyForDone: this.readyForDoneSubject.value,
    });
    
    this.configSubject.next(newConfig);
  }

  /**
   * Determine if agent should be muted
   * Listener agent is always muted (silent)
   */
  private shouldMuteForAgent(agentId: string): boolean {
    // Listener agent is silent - always mute
    if (agentId === 'agent.listener') {
      return true;
    }
    // All other agents are active - don't mute by default
    return false;
  }

  /**
   * Reset to default (listener agent) - called when leaving step components
   * Mutes the widget (listener is silent).
   * Debounced so destroy→next-step updateConfig cancels a useless listener hop.
   */
  resetToListener(delayMs = 500): void {
    if (!this.isEnabled) return;
    if (this.courseEntryWelcomeActive) {
      console.log('[StepAI] skip resetToListener — course entry welcome active');
      return;
    }
    this.cancelPendingResetToListener();
    // Outlet races (FinalTest mount/destroy while Gallery already pushed
    // agent.welcome) must not wipe the winner. Skip if updateConfig ran
    // recently OR after this reset was scheduled.
    const scheduledAt = Date.now();
    const seqAtSchedule = this.flowSeqCounter;
    // Long enough that the next step's updateConfig (after API load) cancels
    // this hop — otherwise we cutover to listener and interrupt the session
    // right before the real step agent starts speaking.
    this.resetToListenerTimer = setTimeout(() => {
      this.resetToListenerTimer = null;
      const current = this.configSubject.value;
      if (this.flowSeqCounter !== seqAtSchedule) {
        console.log('[StepAI] skip resetToListener — superseded by newer config', {
          seqAtSchedule,
          now: this.flowSeqCounter,
          currentAgent: current.agentId,
        });
        return;
      }
      if (this.lastUpdateConfigAt > scheduledAt) {
        console.log('[StepAI] skip resetToListener — updateConfig after schedule', {
          lastUpdateConfigAt: this.lastUpdateConfigAt,
          scheduledAt,
          currentAgent: current.agentId,
        });
        return;
      }
      // A late destroy of the previous step must not park the step on screen
      // once that step has configured its own agent, however long ago.
      if (
        current.agentId &&
        current.agentId !== 'agent.listener' &&
        this.configStepId &&
        this.configStepId === this.activeStepId
      ) {
        console.log('[StepAI] skip resetToListener — current step owns the widget', {
          stepId: this.configStepId,
          currentAgent: current.agentId,
        });
        return;
      }
      // Destroy of a losing outlet often fires AFTER the winning step already
      // configured. Protect a fresh non-listener config (~2s window).
      const msSinceUpdate = Date.now() - this.lastUpdateConfigAt;
      if (
        current.agentId &&
        current.agentId !== 'agent.listener' &&
        current.content &&
        msSinceUpdate >= 0 &&
        msSinceUpdate < 2000
      ) {
        console.log('[StepAI] skip resetToListener — recent step config', {
          currentAgent: current.agentId,
          msSinceUpdate,
        });
        return;
      }
      // Already parked on the silent listener with no flow running: bumping
      // flowSeq again relaunches listenerIdle_v1 for nothing, which is the
      // duplicate launch pair (7 then 8, 10 then 11) in the step log.
      if (current.agentId === 'agent.listener' && !this.aiFlowActive) {
        console.log('[StepAI] skip resetToListener — already parked on listener');
        return;
      }
      console.log('[StepAI] Resetting to listener agent (muted)');
      this.flowSeqCounter += 1;
      const visitId = this.flowSeqCounter;
      this.configStepId = null;
      this.aiFlowActive = false;
      this.clearGateFailOpen();
      this.gateFailedOpen = false;
      this.readyForDoneSubject.next(true);
      this.publishDoneBlocked();
      this.configSubject.next({
        ...this.defaultConfig,
        // Stay visible in the academy drawer; only mute.
        showWidget: current.showWidget !== false,
        muteAi: true,
        flowSeq: visitId,
        stepVisitId: visitId,
      });
    }, delayMs);
  }

  private cancelPendingResetToListener(): void {
    if (this.resetToListenerTimer != null) {
      clearTimeout(this.resetToListenerTimer);
      this.resetToListenerTimer = null;
    }
  }

  /** Called from layout when widget emits ai-chat-flow-terminal. */
  private matchesCurrentCompletion(detail?: CompletionProof): boolean {
    return matchesCompletion(detail, this.audioCapture.currentStepId, this.configSubject.value.flowSeq, this.configSubject.value.completionAttemptId);
  }

  setReadyForDone(ready: boolean, detail?: CompletionProof): void {
    if (!this.isEnabled) return;
    const flowId = String(detail?.flowId || '');
    const reason = String(detail?.reason || '');
    // The welcome is pushed before the step API answers, so it carries no step
    // id and can never pass the Done proof — and it is not a Done decision.
    // Prefer natural resume completion over the host timeout cutover.
    if (
      this.courseEntryWelcomeActive &&
      (flowId === 'resume_v1' || flowId === 'resume') &&
      (ready || reason === 'resume_completed')
    ) {
      this.clearCourseEntryWelcome('resume_terminal');
      return;
    }
    if (!this.matchesCurrentCompletion(detail)) return;
    if (ready && !hasCurrentPass(detail, this.lastVerification)) return;
    this.readyForDoneSubject.next(!!ready);
    if (ready) this.clearGateFailOpen();
    this.publishDoneBlocked();
    console.log('[StepAI] readyForDone', {
      ready: !!ready,
      aiFlowActive: this.aiFlowActive,
      flowId: detail?.flowId,
      reason: detail?.reason,
    });
  }

  /**
   * The company or course opted into mandatory AI validation.
   *
   * Default is false everywhere: AI guidance is help, not a checkpoint, and
   * Done stays clickable whenever the student wants it. Course value wins,
   * then the company feature, then the system flag.
   */
  isStepGateMandatory(): boolean {
    if (this.courseAiStepGate !== null) return this.courseAiStepGate;
    return this.featureSettings.isFeatureEnabled('feature_ai_step_gate');
  }

  /** True when Done may proceed (non-AI, or AI flow reached terminal). */
  isDoneAllowed(): boolean {
    if (['conflict', 'resolved'].includes(this.memorySync$.value.state)) return false;
    if (!this.isEnabled || !this.aiFlowActive) return true;
    // On a guidance-only course the AI is help, not a checkpoint, so Done is
    // the student's to press from the moment the step opens. `isStepGateMandatory`
    // was written as the switch for exactly this but was only ever read inside
    // a console.log, so every step that pushed a worker agent blocked Done —
    // `ai_step_gate: false` did nothing at all.
    if (!this.isStepGateMandatory()) return true;
    if (this.gateFailedOpen) return true;
    return this.readyForDoneSubject.value === true;
  }

  resolveMemory(choice: 'server' | 'local' | 'retry'): void {
    window.dispatchEvent(new CustomEvent('ai-chat-memory-resolve', { detail: { ...this.memorySync$.value, choice } }));
  }

  async resumeSelectedMemory(): Promise<void> {
    try { await this.audioCapture.finalizeBeforeReload(); }
    catch { return; } // Capture service displays the storage failure; do not reload.
    window.location.reload();
  }

  /** Resolved course settings from the enrollment. Cleared on academy exit. */
  setCourseAiSettings(settings?: {
    ai_mode?: CourseAiMode | string | null;
    ai_step_gate?: boolean | null;
    ai_record_audio?: boolean | null;
    ai_semantic_turns?: string | null;
  }): void {
    this.courseAiMode = this.normalizeAiMode(settings?.ai_mode);
    // The config builder is a plain function shared by every step type, so it
    // is told the mode once here rather than through twenty call sites.
    setCourseAiMode(this.courseAiMode);

    this.courseAiStepGate =
      settings?.ai_step_gate === undefined || settings?.ai_step_gate === null
        ? null
        : !!settings.ai_step_gate;
    this.courseAiRecordAudio = !!settings?.ai_record_audio;
    const semantic = settings?.ai_semantic_turns;
    this.courseAiSemanticTurns = semantic === 'shadow' || semantic === 'live' ? semantic : 'off';
    console.log('[StepAI] course ai settings', {
      mode: this.courseAiMode,
      stepGate: this.courseAiStepGate,
      recordAudio: this.courseAiRecordAudio,
      semanticTurns: this.courseAiSemanticTurns,
      mandatory: this.isStepGateMandatory(),
    });

    if (this.courseAiMode === 'off') {
      // Nothing is coming to open a mandatory gate, so release it the same way
      // opting out does; the supervisor reviews this course instead.
      this.readyForDoneSubject.next(true);
      this.clearGateFailOpen();
      this.gateFailedOpen = false;
      this.aiFlowActive = false;
    }

    this.publishDoneBlocked();
    this.publishAiActive();
    this.courseAiSettingsLoaded = true;
    this.courseAiSettingsSubject.next();
  }

  /**
   * An enrollment from a server that predates the mode, or a value someone
   * hand-edited into t_config, must not read as "less AI than authored" —
   * anything unrecognised falls back to the behaviour the steps were built for.
   */
  private normalizeAiMode(mode?: CourseAiMode | string | null): CourseAiMode | null {
    if (mode === null || mode === undefined || mode === '') return null;
    return mode === 'off' || mode === 'simple' ? mode : 'extended';
  }

  /** Course opted into keeping the student's spoken answers on AI steps. */
  isAudioRecordingEnabled(): boolean {
    return this.isEnabled && this.courseAiRecordAudio;
  }

  /** Course's turn-analysis mode; 'off' whenever AI itself is off. */
  getSemanticTurnsMode(): 'off' | 'shadow' | 'live' {
    return this.isEnabled ? this.courseAiSemanticTurns : 'off';
  }

  /**
   * A step type reporting which of its items the student is looking at.
   *
   * Deliberately NOT gated on `aiFlowActive`: a step change parks on the
   * silent listener first, and the gallery reports its starting slide while
   * that hop is still in flight. Dropping it there left the widget with no
   * position at all, so the AI read nothing and asked for Start forever.
   * The widget keeps the last position it was told, so an early report is
   * replayed into the flow when it starts.
   */
  publishStepProgress(progress: StepProgress): void {
    if (!this.isEnabled) return;
    console.log('[StepAI] step progress', {
      slideIndex: progress.slideIndex,
      totalSlides: progress.totalSlides,
      isLastSlide: progress.isLastSlide,
      presentationState: progress.presentationState,
      hasSlideText: !!progress.slideText,
      aiFlowActive: this.aiFlowActive,
    });
    this.lastStepProgress = { stepId: this.activeStepId, progress };
    this.stepProgressSubject.next(progress);
  }

  /**
   * The widget accepted a step visit. Its cutover dropped the position inside
   * the step, and a gallery reports its opening slide before that, so the
   * last position of the step on screen is sent again.
   */
  onStepAccepted(detail: { flowSeq?: number } | null | undefined): void {
    if (!this.isEnabled || typeof detail?.flowSeq !== 'number') return;
    if (detail.flowSeq !== this.configSubject.value.flowSeq) return;
    const last = this.lastStepProgress;
    if (!last || !this.configStepId || last.stepId !== this.configStepId) return;
    console.log('[StepAI] step progress replayed after cutover', {
      flowSeq: detail.flowSeq,
      slideIndex: last.progress.slideIndex,
    });
    this.stepProgressSubject.next(last.progress);
  }

  /** Step that owns the config the widget holds now. */
  getConfigStepId(): string | null {
    return this.configStepId;
  }

  /**
   * Report a graded drill / exam and wait for the AI to finish praising it.
   *
   * Both step types auto-advance the moment the result is saved. Navigating
   * away tears the widget down mid-sentence, so "Congratulations, you did
   * it." would be cut off — the caller awaits this before moving on. The cap
   * keeps a muted or disconnected widget from stalling the course.
   */
  publishQuizResult(result: {
    quizDone: boolean;
    correctPercentage: number;
    wrongCount: number;
  }): Promise<void> {
    if (!this.isEnabled) return Promise.resolve();
    this.publishStepProgress({
      slideIndex: 0,
      totalSlides: 1,
      slideText: '',
      isLastSlide: true,
      quizDone: result.quizDone,
      correctPercentage: result.correctPercentage,
      wrongCount: result.wrongCount,
    });
    return new Promise<void>((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        window.removeEventListener(QUIZ_PRAISE_DONE_EVENT, finish, true);
        resolve();
      };
      const timer = setTimeout(finish, QUIZ_PRAISE_MAX_WAIT_MS);
      // Capture: the widget fires this on its own element, and this listener
      // is on the window that the bubbling composed event passes through.
      window.addEventListener(QUIZ_PRAISE_DONE_EVENT, finish, true);
    });
  }

  /** Called from the layout when the widget asks to turn the page. */
  requestSlide(request: SlideRequest): void {
    this.slideRequestSubject.next(request);
  }

  isAiFlowActive(): boolean {
    return this.aiFlowActive;
  }

  private publishDoneBlocked(): void {
    const blocked = !this.isDoneAllowed();
    if (this.doneBlockedSubject.value !== blocked) {
      this.doneBlockedSubject.next(blocked);
    }
  }

  /**
   * Arm the fail-open timer for a gated AI step. Mirrors the course-entry
   * welcome timeout: a stuck gate should be visible in the log, not a
   * mystery the student cannot get past.
   */
  private armGateFailOpen(): void {
    this.clearGateFailOpen();
    this.gateFailedOpen = false;
    this.verificationUnavailable$.next(null);
    if (!this.aiFlowActive) return;
    this.gateFailOpenTimer = setTimeout(() => {
      this.gateFailOpenTimer = null;
      if (this.readyForDoneSubject.value === true) return;
      console.warn('[StepAI] gate.failOpen — no AI verdict in time, unlocking Done', {
        afterMs: GATE_FAIL_OPEN_MS,
      });
      this.verificationUnavailable$.next('No verification result has arrived. Retry, or explicitly record an unverified exception.');
      this.publishDoneBlocked();
    }, GATE_FAIL_OPEN_MS);
  }

  private clearGateFailOpen(): void {
    if (this.gateFailOpenTimer != null) {
      clearTimeout(this.gateFailOpenTimer);
      this.gateFailOpenTimer = null;
    }
  }

  /**
   * The widget reported it cannot finish: session error, disconnect, or the
   * host muted it. Unlock immediately rather than waiting out the timer.
   */
  reportAiSessionUnavailable(reason: string): void {
    if (!this.aiFlowActive) return;
    if (this.gateFailedOpen) return;
    console.warn('[StepAI] gate.failOpen — AI session unavailable', { reason });
    this.verificationUnavailable$.next(reason);
    this.clearGateFailOpen();
    this.publishDoneBlocked();
  }

  /** An outage never passes an attempt. The learner must explicitly record an exception. */
  async recordUnverifiedException(step: number): Promise<void> {
    const reason = this.verificationUnavailable$.value;
    const visit = this.configSubject.value.flowSeq;
    if (!reason || step !== this.audioCapture.currentStepId) throw new Error('No unavailable verification on this step');
    await firstValueFrom(this.http.post(this.appConfig.get<string>('api_baseurl', '') + '/step-verification/' + step + '/exception', {
      reason, outcome: 'unverified_exception', protocolVersion: 'deterministic-v2', stepVisit: visit,
    }));
    if (step !== this.audioCapture.currentStepId || visit !== this.configSubject.value.flowSeq) return;
    this.gateFailedOpen = true;
    this.verificationUnavailable$.next(null);
    this.publishDoneBlocked();
  }

  /**
   * Mute AI audio (without changing agent)
   */
  mute(): void {
    if (!this.isEnabled) return;
    this.updateConfig({ muteAi: true });
    console.log('🔇 AI muted');
  }

  /**
   * Unmute AI audio (without changing agent)
   */
  unmute(): void {
    if (!this.isEnabled) return;
    const current = this.configSubject.value;
    // Only unmute if not in listener mode
    if (current.agentId !== 'agent.listener') {
      this.updateConfig({ muteAi: false });
      console.log('🔊 AI unmuted');
    } else {
      console.log('⚠️ Cannot unmute - in listener mode (silent agent)');
    }
  }

  /**
   * Toggle mute state
   */
  toggleMute(): void {
    if (!this.isEnabled) return;
    const current = this.configSubject.value;
    if (current.muteAi) {
      this.unmute();
    } else {
      this.mute();
    }
  }

  /**
   * Get current config (synchronous)
   */
  getCurrentConfig(): GlobalAiChatConfig {
    return this.configSubject.value;
  }

  /**
   * Peek resume vs enter without consuming. Call markAcademyWelcomeUsed()
   * only after a real AI worker config is successfully pushed (not listener /
   * non-AI / failed build).
   */
  peekAcademyStepEntry(): 'resume' | 'enter' {
    if (!this.isEnabled) return 'enter';
    return this.academyWelcomeUsed ? 'enter' : 'resume';
  }

  /** One course welcome-back per academy visit. */
  markAcademyWelcomeUsed(): void {
    this.academyWelcomeUsed = true;
  }

  /**
   * Speak course welcome-back as soon as the student enters /academy/*,
   * regardless of step type (FinalTest, Gallery, …). Marks the visit so
   * later AI steps do not repeat “Welcome back to the course …”.
   */
  speakCourseEntryWelcome(step?: {
    title?: string | null;
    caption?: string | null;
    stepId?: string | number | null;
    /** The student has been in this course before — "Welcome back". */
    returnVisit?: boolean;
  } | null): void {
    if (!this.isEnabled) return;
    // Simple mode is one announcement per step and nothing else; the welcome
    // runs its own agent and program, which is exactly what simple opts out of.
    if (this.isSimpleAiMode()) return;
    if (this.academyWelcomeUsed) return;
    const course = this.academyCourseTitle;
    if (!course) {
      console.warn('[StepAI] course entry welcome skipped — no course title yet');
      return;
    }

    const welcomeStepId =
      step?.stepId != null && String(step.stepId).trim() !== ''
        ? String(step.stepId)
        : null;

    const strip = (s: string) =>
      String(s || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    const stepLabel =
      strip(step?.caption || '') ||
      strip(step?.title || '') ||
      'this step';

    // Greeting only. Naming the step and its Done control here just made the
    // student sit through an orientation the step's own AI config is about
    // to speak anyway.
    const returnVisit = !!step?.returnVisit;
    const command = (
      returnVisit
        ? `Welcome back to the course ${course}.`
        : `Welcome to the course ${course}.`
    )
      .replace(/\s+/g, ' ')
      .trim();

    this.academyWelcomeUsed = true;
    this.courseEntryWelcomeActive = true;
    this.courseEntryWelcomeStepId = welcomeStepId;
    this.pendingConfigAfterWelcome = null;
    this.pendingConfigStepId = null;
    // Prefer the checksheet-open step as soon as we know it — blocks stale
    // outlet configs from the previous URL step while router catches up.
    if (welcomeStepId) {
      this.activeStepId = welcomeStepId;
    }
    this.cancelPendingResetToListener();
    if (this.courseEntryWelcomeTimer != null) {
      clearTimeout(this.courseEntryWelcomeTimer);
    }
    // If the widget never flushes/speaks (connect race), do not block forever.
    // While CONNECTED + resume is still speaking, extend instead of cutting over
    // to muted listener mid-line (was forcing FinalTest quiet while audio drained).
    const welcomeArmedAt = Date.now();
    const MAX_COURSE_ENTRY_MS = 90_000;
    const armCourseEntryWelcomeTimer = (ms: number) => {
      if (this.courseEntryWelcomeTimer != null) {
        clearTimeout(this.courseEntryWelcomeTimer);
      }
      this.courseEntryWelcomeTimer = setTimeout(() => {
        this.courseEntryWelcomeTimer = null;
        if (!this.courseEntryWelcomeActive) return;
        const status = String(
          (window as any)?.__aiChatZustandState?.sessionStatus ||
            (window as any)?.__useAppState?.getState?.()?.sessionStatus ||
            ''
        );
        const connected = status === 'CONNECTED';
        const elapsed = Date.now() - welcomeArmedAt;
        if (connected && elapsed < MAX_COURSE_ENTRY_MS) {
          console.warn(
            '[StepAI] course entry welcome still active while CONNECTED — extending deferral',
            { elapsed }
          );
          armCourseEntryWelcomeTimer(20_000);
          return;
        }
        console.warn('[StepAI] course entry welcome timed out — releasing deferral', {
          elapsed,
          connected,
        });
        this.clearCourseEntryWelcome('timeout');
      }, ms);
    };
    armCourseEntryWelcomeTimer(25_000);
    this.updateConfig({
      agentId: 'agent.welcome',
      muteAi: false,
      content: JSON.stringify({
        command,
        stepTitle: stepLabel,
        entryReason: 'resume',
        courseEntry: true,
      }),
      sourceStepId: welcomeStepId,
    });
    console.log('[StepAI] course entry welcome pushed', {
      course,
      stepLabel,
      welcomeStepId,
      commandLength: command.length,
    });
  }

  /** True while course-entry welcome must not be interrupted. */
  isCourseEntryWelcomeActive(): boolean {
    return this.courseEntryWelcomeActive;
  }

  private contentHasCourseEntry(content?: string | null): boolean {
    if (!content) return false;
    try {
      const parsed = JSON.parse(content);
      return parsed?.courseEntry === true;
    } catch {
      return false;
    }
  }

  /**
   * End course-entry protection and apply any deferred step config
   * (or fall back to muted listener on non-AI landings like FinalTest).
   */
  clearCourseEntryWelcome(reason = 'manual'): void {
    if (!this.courseEntryWelcomeActive && !this.pendingConfigAfterWelcome) return;
    this.courseEntryWelcomeActive = false;
    if (this.courseEntryWelcomeTimer != null) {
      clearTimeout(this.courseEntryWelcomeTimer);
      this.courseEntryWelcomeTimer = null;
    }
    const pending = this.pendingConfigAfterWelcome;
    const pendingStepId = this.pendingConfigStepId;
    const welcomeStepId = this.courseEntryWelcomeStepId;
    this.pendingConfigAfterWelcome = null;
    this.pendingConfigStepId = null;
    this.courseEntryWelcomeStepId = null;
    // Strict: only apply deferred config when it is tagged for the welcomed step.
    const pendingMatchesWelcome =
      !!pending &&
      !!pendingStepId &&
      !!welcomeStepId &&
      pendingStepId === welcomeStepId &&
      (!this.activeStepId || pendingStepId === this.activeStepId);
    console.log('[StepAI] course entry welcome cleared', {
      reason,
      hasPending: !!pending,
      pendingStepId,
      welcomeStepId,
      activeStepId: this.activeStepId,
      applyPending: pendingMatchesWelcome,
    });
    if (pending && pendingMatchesWelcome) {
      // Brief settle so the last words of course-entry welcome can finish
      // draining before a deferred step config cutover interrupts audio.
      setTimeout(() => {
        if (this.courseEntryWelcomeActive) return;
        this.updateConfig({
          ...pending,
          sourceStepId: pendingStepId,
        });
      }, 500);
    } else {
      if (pending && !pendingMatchesWelcome) {
        console.log('[StepAI] drop pending after welcome — step mismatch', {
          pendingStepId,
          welcomeStepId,
          activeStepId: this.activeStepId,
        });
      }
      // Non-AI landing (FinalTest / drill): keep the welcome orientation as
      // listener context instead of wiping content to length 0.
      this.settleQuietListenerAfterWelcome();
    }
  }

  /**
   * After course-entry welcome on a step with no AI worker config: stay on the
   * muted listener but keep the welcome command so the AI still knows where
   * the student is.
   */
  private settleQuietListenerAfterWelcome(): void {
    const current = this.configSubject.value;
    let content = current.content;
    try {
      const parsed = JSON.parse(String(current.content || ''));
      if (parsed && typeof parsed === 'object') {
        content = JSON.stringify({
          command: parsed.command || undefined,
          stepTitle: parsed.stepTitle || undefined,
          entryReason: 'enter',
        });
      }
    } catch {
      /* keep as-is */
    }
    this.aiFlowActive = false;
    this.clearGateFailOpen();
    this.gateFailedOpen = false;
    this.readyForDoneSubject.next(true);
    this.publishDoneBlocked();

    // Same settle as the deferred-config branch above. This runs inside the
    // synchronous ai-chat-flow-terminal emit, so pushing muteAi:true here
    // would re-enter the widget from inside completeFlow and hard-mute the
    // last words of the welcome.
    setTimeout(() => {
      if (this.courseEntryWelcomeActive) return;
      // A newer step config landed while we settled — it owns the widget now.
      if (this.configSubject.value !== current) return;
      this.cancelPendingResetToListener();
      this.flowSeqCounter += 1;
      const visitId = this.flowSeqCounter;
      this.lastUpdateConfigAt = Date.now();
      this.configSubject.next({
        ...this.defaultConfig,
        apiBaseUrl: current.apiBaseUrl || this.defaultConfig.apiBaseUrl,
        showWidget: current.showWidget !== false,
        muteAi: true,
        content,
        flowSeq: visitId,
        stepVisitId: visitId,
      });
      console.log('[StepAI] course entry welcome → quiet listener', {
        contentLength: content?.length || 0,
      });
    }, 500);
  }

  /**
   * @deprecated Prefer peekAcademyStepEntry + markAcademyWelcomeUsed so
   * listener / failed builds do not burn the resume latch.
   */
  consumeAcademyStepEntry(): 'resume' | 'enter' {
    return this.peekAcademyStepEntry();
  }

  /** Current course title for “Welcome back to the course …”. */
  setAcademyCourseTitle(title: string | null | undefined): void {
    this.academyCourseTitle = String(title || '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  getAcademyCourseTitle(): string {
    return this.academyCourseTitle;
  }

  /**
   * The course (t_ot_course) the open enrollment belongs to. Not the
   * enrollment id in the /academy/{id} URL: course-scoped AI knowledge is
   * keyed by this one.
   */
  setAcademyCourseId(courseId: number | string | null | undefined): void {
    const id = Number(courseId);
    this.academyCourseId = Number.isFinite(id) && id > 0 ? String(id) : '';
  }

  getAcademyCourseId(): string {
    return this.academyCourseId;
  }

  /** 1-based position of the open step in the checksheet. */
  setAcademyStepPosition(position: number | null, total: number | null): void {
    setCourseStepPosition(position, total);
    this.stepNavigationAt = Date.now();
  }

  /**
   * Widget reported a step visit's first audio. One line per step, splitting
   * step load (navigation → push), cutover (push → accepted) and the first
   * line (accepted → audio).
   */
  reportStepTiming(detail: { flowSeq?: number; flowId?: string; acceptedAt?: number; firstAudioAt?: number } | null | undefined): void {
    if (!detail || typeof detail.flowSeq !== 'number') return;
    const pushedAt = this.configPushedAt.get(detail.flowSeq);
    const { acceptedAt, firstAudioAt } = detail;
    const span = (from?: number, to?: number) =>
      typeof from === 'number' && from > 0 && typeof to === 'number' && to >= from ? to - from : null;
    const navigatedAt = this.stepNavigationAt > 0 && pushedAt && pushedAt >= this.stepNavigationAt
      ? this.stepNavigationAt
      : undefined;
    console.log('[StepAI] step.latency', {
      flowSeq: detail.flowSeq,
      flowId: detail.flowId,
      navToPushMs: span(navigatedAt, pushedAt),
      pushToAcceptMs: span(pushedAt, acceptedAt),
      acceptToAudioMs: span(acceptedAt, firstAudioAt),
      navToAudioMs: span(navigatedAt, firstAudioAt),
    });
  }

  /** Reset when leaving /academy/* so the next course entry can welcome again. */
  resetAcademyVisit(): void {
    this.academyWelcomeUsed = false;
    this.courseDictionary = [];
    this.academyCourseTitle = '';
    this.academyCourseId = '';
    setCourseStepPosition(null);
    // Course-scoped settings must not leak into the next course.
    this.courseAiMode = null;
    this.courseAiSettingsLoaded = false;
    setCourseAiMode(null);
    this.courseAiStepGate = null;
    this.courseAiRecordAudio = false;
    this.courseAiSemanticTurns = 'off';
    this.audioCapture.clearRefusedSteps();
    this.publishAiActive();
    this.clearGateFailOpen();
    this.gateFailedOpen = false;
    this.publishDoneBlocked();
    this.courseEntryWelcomeActive = false;
    this.courseEntryWelcomeStepId = null;
    this.activeStepId = null;
    this.pendingConfigAfterWelcome = null;
    this.pendingConfigStepId = null;
    if (this.courseEntryWelcomeTimer != null) {
      clearTimeout(this.courseEntryWelcomeTimer);
      this.courseEntryWelcomeTimer = null;
    }
  }

  /**
   * Check if currently muted
   */
  isMuted(): boolean {
    return this.configSubject.value.muteAi;
  }

  /**
   * Clear widget (hide it)
   */
  hideWidget(): void {
    if (!this.isEnabled) return;
    this.updateConfig({ showWidget: false });
  }

  /**
   * Show widget
   */
  showWidget(): void {
    if (!this.isEnabled) return;
    this.updateConfig({ showWidget: true });
  }
}
